import React from "react";
import { format } from "date-fns";
import { cycleTimeline, computeIntake, phaseForDate } from "../lib/cycle";
import { useI18n } from "../i18n";
import CycleCalendar from "./CycleCalendar";
import PhaseArticle from "./PhaseArticle";
import InfoArticle from "./InfoArticle";
import { METRIC_ICONS, directionMeta, focusMeta, LevelDots, Droplet, Beef } from "./metricUtils";
import { ArrowUp, Minus, ArrowDown, ChevronRight } from "lucide-react";

const METRIC_KEY_MAP = { activity: "movement", carbs: "carbs", fat: "fats" };

function PhaseRow({ phaseKey, days, isCurrent }) {
  const { phases, t, dateLocale } = useI18n();
  const p = phases[phaseKey];
  const fMeta = focusMeta(p.focus);
  const startD = days[0].date;
  const endD = days[days.length - 1].date;
  const metrics = ["activity", "carbs", "fat"];

  return (
    <PhaseArticle phase={p}>
      <div
        data-testid={`summary-row-${p.key}`}
        role="button"
        tabIndex={0}
        className="bg-bone border rounded-2xl p-4 relative cursor-pointer transition-transform hover:-translate-y-0.5 active:scale-[0.99]"
        style={{ borderColor: isCurrent ? p.color : "hsl(var(--border))", borderWidth: isCurrent ? 2 : 1 }}
      >
        <div className="flex items-center gap-3 mb-3">
          <span className="h-9 w-1.5 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-serif text-xl leading-none" style={{ color: p.color }}>{p.label}</h4>
              {isCurrent && (
                <span className="text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: p.color }}>
                  {t("now")}
                </span>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              {format(startD, "MMM d", { locale: dateLocale })} – {format(endD, "MMM d", { locale: dateLocale })} · {t("daysCount", { n: days.length })}
            </div>
          </div>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full" style={{ color: fMeta.color, backgroundColor: fMeta.color + "1A" }}>
            <fMeta.Icon className="h-3 w-3" />
            {t(p.focus === "fitness" ? "fitness" : "hormones")}
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        </div>

        <div className="grid grid-cols-3 gap-2">
          {metrics.map((m) => {
            const Icon = METRIC_ICONS[m];
            const dir = directionMeta(p[m].direction);
            return (
              <div key={m} className="rounded-xl border border-border p-2.5 flex flex-col items-center gap-1.5">
                <div className="flex items-center gap-1">
                  <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                  <dir.Icon className="h-3 w-3" style={{ color: dir.color }} />
                </div>
                <LevelDots level={p[m].level} color={dir.color} testid={`summary-${p.key}-${m}`} />
                <span className="text-[10px] font-semibold text-muted-foreground tracking-wide">{t(METRIC_KEY_MAP[m])}</span>
              </div>
            );
          })}
        </div>
      </div>
    </PhaseArticle>
  );
}

export default function MonthlySummary({ data }) {
  const { water, protein, t, dateLocale } = useI18n();
  const { days, start } = cycleTimeline(data);
  const intake = computeIntake(data.weight);
  const proteinMax = Math.round(Number(data.weight) * 1.15);
  const currentPhaseKey = phaseForDate(new Date(), data).phaseKey;

  const groups = [];
  days.forEach((d) => {
    const last = groups[groups.length - 1];
    if (last && last.key === d.phaseKey) last.days.push(d);
    else groups.push({ key: d.phaseKey, days: [d] });
  });

  const end = new Date(start);
  end.setDate(start.getDate() + days.length - 1);

  return (
    <div className="space-y-4 animate-fade-up">
      <div data-testid="summary-header" className="bg-bone border border-border rounded-3xl p-6">
        <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-primary">{t("currentCycle")}</div>
        <h2 className="font-serif text-3xl mt-1.5">
          {format(start, "MMM d", { locale: dateLocale })} – {format(end, "MMM d", { locale: dateLocale })}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {t("cyclePeriodInfo", { c: data.cycleLength, p: data.periodLength })}
        </p>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <InfoArticle article={water} color="#3E7CB1" label={t("hydrationLabel")} testid="water-article-summary">
            <div role="button" tabIndex={0} data-testid="water-card-trigger-summary" className="flex items-center gap-2 rounded-xl border border-border p-3 cursor-pointer transition-transform hover:-translate-y-0.5 active:scale-[0.99]">
              <Droplet className="h-4 w-4 text-[#3E7CB1] shrink-0" />
              <div className="min-w-0">
                <div className="font-serif text-xl leading-none">{intake.waterOz} <span className="text-xs font-sans text-muted-foreground">{t("flOz")}</span></div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("dailyWater")}</div>
                <div className="text-[10px] font-bold text-[#3E7CB1] mt-0.5 inline-flex items-center gap-0.5">{t("whyThisMatters")} <ChevronRight className="h-2.5 w-2.5" /></div>
              </div>
            </div>
          </InfoArticle>
          <InfoArticle article={protein} color="#9B3926" label={t("nutritionLabel")} testid="protein-article-summary">
            <div role="button" tabIndex={0} data-testid="protein-card-trigger-summary" className="flex items-center gap-2 rounded-xl border border-border p-3 cursor-pointer transition-transform hover:-translate-y-0.5 active:scale-[0.99]">
              <Beef className="h-4 w-4 text-primary shrink-0" />
              <div className="min-w-0">
                <div className="font-serif text-xl leading-none">{intake.proteinG}<span className="text-base">–{proteinMax}</span> <span className="text-xs font-sans text-muted-foreground">{t("grams")}</span></div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("dailyProtein")}</div>
                <div className="text-[10px] font-bold text-primary mt-0.5 inline-flex items-center gap-0.5">{t("whyThisMatters")} <ChevronRight className="h-2.5 w-2.5" /></div>
              </div>
            </div>
          </InfoArticle>
        </div>
        <p className="text-[11px] text-muted-foreground mt-2 leading-snug">{t("proteinRangeNote")}</p>
      </div>

      <CycleCalendar data={data} />

      <div className="space-y-3">
        {groups.map((g, i) => (
          <PhaseRow key={g.key + i} phaseKey={g.key} days={g.days} isCurrent={g.key === currentPhaseKey} />
        ))}
      </div>

      <div data-testid="summary-legend" className="bg-bone border border-border rounded-2xl p-5">
        <div className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-3">{t("howToRead")}</div>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="flex flex-col items-center gap-1 text-center">
            <METRIC_ICONS.activity className="h-4 w-4 text-foreground/70" />
            <span className="text-[11px] font-semibold">{t("movement")}</span>
          </div>
          <div className="flex flex-col items-center gap-1 text-center">
            <METRIC_ICONS.carbs className="h-4 w-4 text-foreground/70" />
            <span className="text-[11px] font-semibold">{t("carbs")}</span>
          </div>
          <div className="flex flex-col items-center gap-1 text-center">
            <METRIC_ICONS.fat className="h-4 w-4 text-foreground/70" />
            <span className="text-[11px] font-semibold">{t("fats")}</span>
          </div>
        </div>
        <div className="space-y-2 border-t border-border pt-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ArrowUp className="h-3.5 w-3.5" style={{ color: "#2E7D32" }} />
            <span className="font-semibold text-foreground/80">{t("increase")}</span> — {t("increaseHint")}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Minus className="h-3.5 w-3.5" style={{ color: "#B0872F" }} />
            <span className="font-semibold text-foreground/80">{t("maintain")}</span> — {t("maintainHint")}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ArrowDown className="h-3.5 w-3.5" style={{ color: "#9B3926" }} />
            <span className="font-semibold text-foreground/80">{t("reduce")}</span> — {t("reduceHint")}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
            <span className="flex items-center gap-1">
              <span className="h-1.5 w-3.5 rounded-full" style={{ backgroundColor: "#2D2A26" }} />
              <span className="h-1.5 w-3.5 rounded-full" style={{ backgroundColor: "#E5DFD3" }} />
              <span className="h-1.5 w-3.5 rounded-full" style={{ backgroundColor: "#E5DFD3" }} />
            </span>
            <span>{t("dotsHint")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
